import { useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import "@/App.css";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Compass,
  Gauge,
  Hammer,
  Home,
  Layers3,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  PencilRuler,
  Phone,
  Ruler,
  ShieldCheck,
  Store,
  X,
} from "lucide-react";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1774685110718-c5b4fe026144?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBob21lJTIwZXh0ZXJpb3IlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc1MDc5ODk5fDA&ixlib=rb-4.1.0&q=85",
  project1: "https://images.unsplash.com/photo-1622015663319-e97e697503ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBob21lJTIwZXh0ZXJpb3IlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc1MDc5ODk5fDA&ixlib=rb-4.1.0&q=85",
  project2: "https://images.unsplash.com/photo-1766603636700-e9d80473f40f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsdXh1cnklMjBob21lJTIwZXh0ZXJpb3IlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc1MDc5ODk5fDA&ixlib=rb-4.1.0&q=85",
  project3: "https://images.pexels.com/photos/7031408/pexels-photo-7031408.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  project4: "https://images.unsplash.com/photo-1760072513442-9872656c1b07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtaW5pbWFsaXN0JTIwaW50ZXJpb3IlMjBkZXNpZ258ZW58MHx8fHwxNzc1MDc5ODM1fDA&ixlib=rb-4.1.0&q=85",
  whyUs: "https://images.pexels.com/photos/8961031/pexels-photo-8961031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  blueprints: "https://images.pexels.com/photos/8470842/pexels-photo-8470842.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const CONTACT = {
  phone: "+54 9 2901 40-9188",
  whatsapp: "5492901409188",
  email: "morcatc@gmail.com",
  address: "Transporte Villarino 474, Ushuaia",
};

const NAV_LINKS = [
  { id: "inicio", name: "Inicio" },
  { id: "servicios", name: "Servicios" },
  { id: "proyectos", name: "Proyectos" },
  { id: "estimador", name: "Estimador" },
  { id: "proceso", name: "Proceso" },
  { id: "nosotros", name: "Nosotros" },
  { id: "contacto", name: "Contacto" },
];

const SECTION_IDS = NAV_LINKS.map((link) => link.id);

const STATS = [
  {
    value: 15,
    suffix: "+",
    label: "años en Ushuaia",
    detail: "Conocemos el clima, los materiales y los gremios de Tierra del Fuego.",
  },
  {
    value: 52,
    suffix: "+",
    label: "obras entregadas",
    detail: "Viviendas, remodelaciones y locales. Llave en mano, en fecha.",
  },
  {
    value: 97,
    suffix: "%",
    label: "clientes referidos",
    detail: "La mayoría de los proyectos llegan por recomendación directa.",
  },
];

const SERVICES = [
  {
    id: "residencial",
    icon: Home,
    title: "Viviendas a medida",
    summary: "Casa llave en mano, desde el proyecto hasta la entrega. Presupuesto cerrado y seguimiento semanal para que sepas cómo avanza tu obra.",
    lead: "Para quienes quieren saber en todo momento qué está pasando, sin tener que perseguir a nadie.",
    outcomes: [
      "Proyecto ejecutivo y estrategia constructiva desde el arranque.",
      "Coordinación de todos los gremios, compras y entregas por hitos.",
      "Control de terminaciones, documentación y soporte post-entrega.",
    ],
    metrics: ["120 a 320 m²", "8 a 14 meses", "Control semanal"],
  },
  {
    id: "remodelacion",
    icon: Hammer,
    title: "Remodelaciones integrales",
    summary: "Reformas completas sin que tu vida se paralice. Etapas claras, fechas reales y presencia diaria en obra.",
    lead: "Ideal cuando seguís viviendo o trabajando en el lugar mientras se remodelan.",
    outcomes: [
      "Demoliciones secuenciadas y protección de sectores en uso.",
      "Estructura, instalaciones nuevas y carpinterías a medida.",
      "Entrega por ambientes con checklist de cierre.",
    ],
    metrics: ["60 a 180 m²", "3 a 7 meses", "Por etapas"],
  },
  {
    id: "ampliaciones",
    icon: Ruler,
    title: "Ampliaciones y anexos",
    summary: "Metros nuevos que se integran al resto de la casa como si siempre hubieran estado. Sin remiendos ni improvisaciones.",
    lead: "La ampliación tiene que nacer con la casa, no pegarse encima.",
    outcomes: [
      "Relevamiento del estado actual y revisión de estructura existente.",
      "Compatibilización entre obra nueva, cubierta e instalaciones.",
      "Transición limpia entre lo nuevo y lo existente.",
    ],
    metrics: ["20 a 90 m²", "2 a 5 meses", "Mínima interrupción"],
  },
  {
    id: "seco",
    icon: PencilRuler,
    title: "Construcción en seco",
    summary: "Más velocidad, mejor aislación térmica y menos humedad. Ideal para el clima extremo de Ushuaia.",
    lead: "Aprovechamos las ventanas cortas de buen clima con un montaje rápido y muy preciso.",
    outcomes: [
      "Ingeniería de panelizado y resolución de puentes térmicos.",
      "Capas de aislación y barreras según normas patagónicas.",
      "Montaje acelerado con seguimiento fotográfico por etapa.",
    ],
    metrics: ["Alta velocidad", "Menor humedad", "Mejor aislación"],
  },
  {
    id: "comercial",
    icon: Store,
    title: "Espacios comerciales",
    summary: "Locales y oficinas listos para abrir. Cumplimos fechas porque sabemos que cada día cerrado cuesta.",
    lead: "No solo construimos: resolvemos circulación, mantenimiento y que todo funcione desde el día uno.",
    outcomes: [
      "Layouts comerciales, mobiliario integrado y fachada.",
      "Instalaciones pensadas para operación diaria real.",
      "Cronograma ajustado a tu fecha de apertura.",
    ],
    metrics: ["Retail y oficinas", "Entrega puntual", "Apertura asistida"],
  },
  {
    id: "control",
    icon: Building2,
    title: "Dirección de obra",
    summary: "Si ya tenés tu proyecto y necesitás que alguien lo lleve bien, nosotros somos esa opción.",
    lead: "Presencia en obra, control de calidad, decisiones a tiempo y reporte semanal para el cliente.",
    outcomes: [
      "Planificación, reuniones técnicas y seguimiento de desvíos.",
      "Control de presupuesto, cambios y compras críticas.",
      "Reportes ejecutivos con próximos hitos visibles.",
    ],
    metrics: ["Reportes semanales", "Sin sorpresas", "Decisiones trazables"],
  },
];

const PROJECT_FILTERS = ["Todos", "Residencial", "Remodelación", "Interior"];

const PROJECTS = [
  {
    id: "lapataia",
    category: "Residencial",
    image: IMAGES.project1,
    title: "Residencia Lapataia",
    type: "Vivienda unifamiliar",
    location: "Ushuaia",
    area: "280 m²",
    year: "2024",
    duration: "11 meses",
    badge: "Llave en mano",
    summary: "Casa de lenguaje contemporáneo con envolvente optimizada para viento, humedad y amplitud térmica.",
    scope: [
      "Proyecto ejecutivo coordinado con estructura e instalaciones.",
      "Interiores cálidos con carpintería a medida y detalles en madera.",
      "Seguimiento semanal con reportes visuales al cliente.",
    ],
    highlight: "Secuencia de obra diseñada para proteger terminaciones y evitar retrabajos.",
  },
  {
    id: "bosque",
    category: "Remodelación",
    image: IMAGES.project2,
    title: "Casa del Bosque",
    type: "Remodelación integral",
    location: "Ushuaia",
    area: "160 m²",
    year: "2023",
    duration: "5 meses",
    badge: "Obra por etapas",
    summary: "Transformación completa de una casa existente, reordenando circulación, envolvente y áreas sociales.",
    scope: [
      "Demolición selectiva y reconfiguración estructural.",
      "Renovación integral de cocina, baños e instalaciones.",
      "Plan de intervención pensado para reducir tiempos fuera de uso.",
    ],
    highlight: "Cada etapa se liberó con checklist técnico antes de avanzar a la siguiente.",
  },
  {
    id: "andina",
    category: "Residencial",
    image: IMAGES.project3,
    title: "Villa Andina",
    type: "Obra nueva",
    location: "Ushuaia",
    area: "210 m²",
    year: "2025",
    duration: "9 meses",
    badge: "Sistema mixto",
    summary: "Proyecto con una estructura eficiente, aperturas generosas y una ejecución de detalle muy controlada.",
    scope: [
      "Base modular para acelerar estructura y cerramientos.",
      "Resolución de vistas, privacidad y luz natural.",
      "Acompañamiento al cliente en decisiones de terminación.",
    ],
    highlight: "El layout se ajustó durante obra sin perder control de presupuesto ni plazo.",
  },
  {
    id: "centro",
    category: "Interior",
    image: IMAGES.project4,
    title: "Departamento Centro",
    type: "Remodelación interior",
    location: "Ushuaia",
    area: "95 m²",
    year: "2024",
    duration: "10 semanas",
    badge: "Entrega rápida",
    summary: "Actualización integral de un departamento urbano con foco en sofisticación visual y eficiencia espacial.",
    scope: [
      "Reconfiguración completa de cocina, living y suite principal.",
      "Carpinterías nuevas, iluminación en capas y paleta sobria.",
      "Coordinación milimétrica para una obra corta y muy visible.",
    ],
    highlight: "Se trabajó con hitos de compra y montaje para llegar a una fecha de mudanza fija.",
  },
];

const PROCESS_STEPS = [
  {
    id: "brief",
    number: "01",
    title: "Nos reunimos y escuchamos",
    summary: "Antes de dibujar nada: entendemos el proyecto, el presupuesto real y los tiempos que tenés.",
    owner: "Dirección de proyecto",
    cadence: "Respuesta inicial en 48 hs",
    deliverables: [
      "Resumen de necesidades y prioridades reales.",
      "Revisión temprana de riesgos del terreno o inmueble.",
      "Siguiente paso claro para avanzar sin vueltas.",
    ],
  },
  {
    id: "strategy",
    number: "02",
    title: "Armamos la estrategia",
    summary: "Elegimos el sistema constructivo, la secuencia y las compras críticas. Todo antes de arrancar.",
    owner: "Equipo técnico",
    cadence: "Revisión conjunta con el cliente",
    deliverables: [
      "Plan de obra con cronograma real.",
      "Alcance técnico por rubro sin letra chica.",
      "Presupuesto cerrado con rangos claros.",
    ],
  },
  {
    id: "execution",
    number: "03",
    title: "Ejecutamos con seguimiento visible",
    summary: "La obra avanza con hitos, fotos y reporte semanal. Sin silencio, sin sorpresas.",
    owner: "Jefatura de obra",
    cadence: "Reporte semanal + control diario",
    deliverables: [
      "Fotos, avances y próximos pasos cada semana.",
      "Gestión de cambios con impacto claro en costo y plazo.",
      "Control de calidad en cada frente de trabajo.",
    ],
  },
  {
    id: "handover",
    number: "04",
    title: "Entregamos y respaldamos",
    summary: "No entregamos la llave y desaparecemos. La obra queda documentada y con garantía por escrito.",
    owner: "Dirección + cliente",
    cadence: "Checklist de cierre por ambientes",
    deliverables: [
      "Recorrida final y lista de detalles antes de firmar.",
      "Documentación técnica y garantías entregadas.",
      "Soporte post-entrega para ajustes finos.",
    ],
  },
];

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Garantía por escrito",
    description: "Firmamos y respaldamos cada trabajo. No hacemos promesas de palabra: todo queda documentado.",
  },
  {
    icon: Compass,
    title: "Presupuesto cerrado",
    description: "El precio que acordamos es el precio final. Sin incrementos sorpresa ni costos extra al avanzar.",
  },
  {
    icon: Layers3,
    title: "Plazos en contrato",
    description: "Ponemos las fechas por escrito. Si hay un cambio, te lo decimos con tiempo y con contexto real.",
  },
  {
    icon: Gauge,
    title: "Dueños en obra",
    description: "No mandamos a un tercero. Los dueños de MORCAT están presentes en cada proyecto todos los días.",
  },
];

const TESTIMONIALS = [
  {
    name: "María L.",
    role: "Vivienda unifamiliar, Ushuaia",
    quote: "Fueron los únicos que cumplieron con el plazo. Nos entregaron la casa en fecha y sin un peso de más. Desde el primer día supimos qué seguía y por qué.",
  },
  {
    name: "Carlos R.",
    role: "Remodelación de local comercial",
    quote: "Les pedí remodelar el local y lo terminaron antes de lo previsto. El dueño estaba ahí todos los días. Profesionales de punta a punta.",
  },
  {
    name: "Ana M.",
    role: "Ampliación en Andorra, Ushuaia",
    quote: "Tuve malas experiencias antes. Con MORCAT fue otra cosa: presupuesto cerrado, materiales de primera y cero drama. Los volvería a elegir.",
  },
];

const FAQ_DATA = [
  {
    question: "¿Trabajan solo en obras grandes?",
    answer: "No. Tomamos obras integrales, remodelaciones puntuales, ampliaciones y dirección técnica. Lo importante es definir bien el alcance desde el arranque para trabajar con presupuesto cerrado.",
  },
  {
    question: "¿Cómo manejan el presupuesto y los cambios?",
    answer: "Trabajamos con presupuesto cerrado. Si durante la obra aparece un cambio, te lo presentamos con su impacto real en costo y plazo antes de ejecutarlo. Sin sorpresas.",
  },
  {
    question: "¿Construyen en sistema tradicional y en seco?",
    answer: "Sí. Usamos el sistema que mejor le conviene a tu proyecto según el plazo, el clima y el desempeño que buscás. No vendemos un solo sistema para todo.",
  },
  {
    question: "¿Pueden gestionar los permisos de obra?",
    answer: "Sí. Coordinamos con profesionales matriculados para permisos, planos y toda la documentación que necesitás para construir con respaldo técnico y legal.",
  },
  {
    question: "¿Con qué frecuencia informan sobre el avance?",
    answer: "Hay control diario en obra y reporte semanal al cliente. En hitos críticos, la comunicación es inmediata para tomar decisiones sin frenar la obra.",
  },
  {
    question: "¿Qué garantía dan después de entregar?",
    answer: "Damos garantía por escrito. Después de la entrega seguimos disponibles para ajustes finos y cualquier consulta técnica que surja.",
  },
];

const PROJECT_TYPES = {
  vivienda: {
    label: "Vivienda nueva",
    baseRate: 1280,
    leadTime: 8,
    system: "Sistema mixto con foco en aislación y envolvente.",
  },
  remodelacion: {
    label: "Remodelación integral",
    baseRate: 930,
    leadTime: 4,
    system: "Intervención por etapas para reducir interferencias.",
  },
  ampliacion: {
    label: "Ampliación",
    baseRate: 980,
    leadTime: 3,
    system: "Compatibilización fina con estructura existente.",
  },
  seco: {
    label: "Construcción en seco",
    baseRate: 1150,
    leadTime: 6,
    system: "Montaje acelerado con control milimétrico.",
  },
};

const FINISH_OPTIONS = {
  esencial: { label: "Esencial", multiplier: 1, detail: "Buen desempeño y selección eficiente." },
  premium: { label: "Premium", multiplier: 1.18, detail: "Más detalle, mejores terminaciones y carpintería." },
  signature: { label: "Signature", multiplier: 1.34, detail: "Diseño más fino, customización y piezas especiales." },
};

const RHYTHM_OPTIONS = {
  flexible: { label: "Flexible", multiplier: 1, monthsDelta: 1, detail: "Más margen de decisión y compras." },
  firme: { label: "Ritmo firme", multiplier: 1.08, monthsDelta: 0, detail: "Cadencia balanceada para obra controlada." },
  acelerado: { label: "Acelerado", multiplier: 1.16, monthsDelta: -1, detail: "Más presión logística y decisiones rápidas." },
};

const usdFormatter = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function scrollToSection(sectionId) {
  const section = document.getElementById(sectionId);
  if (!section) return;

  const top = section.getBoundingClientRect().top + window.scrollY - 104;
  window.scrollTo({
    top,
    behavior: "smooth",
  });
}

function calculateEstimate({ projectType, area, finish, rhythm }) {
  const type = PROJECT_TYPES[projectType];
  const finishConfig = FINISH_OPTIONS[finish];
  const rhythmConfig = RHYTHM_OPTIONS[rhythm];

  const base = area * type.baseRate * finishConfig.multiplier * rhythmConfig.multiplier;
  const reserve = base * 0.09;
  const min = Math.round(base);
  const max = Math.round(base + reserve);
  const timeline = Math.max(2, Math.round(type.leadTime + area / 42 + rhythmConfig.monthsDelta));
  const complexity = Math.min(
    100,
    Math.round(32 + area / 4 + finishConfig.multiplier * 18 + rhythmConfig.multiplier * 11),
  );

  return {
    min,
    max,
    timeline,
    complexity,
    reserve: Math.round(reserve),
    system: type.system,
    scopeLabel: type.label,
  };
}

function useInViewOnce(options = { threshold: 0.25 }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isVisible) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, options);

    const node = ref.current;
    if (node) {
      observer.observe(node);
    }

    return () => observer.disconnect();
  }, [isVisible, options]);

  return [ref, isVisible];
}

function useCountUp(target, start) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) return undefined;

    let frameId = 0;
    const duration = 1200;
    const startTime = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      setValue(Math.round(target * (1 - (1 - progress) ** 3)));

      if (progress < 1) {
        frameId = window.requestAnimationFrame(tick);
      }
    };

    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [start, target]);

  return value;
}

function useActiveSection(sectionIds) {
  const [activeSection, setActiveSection] = useState(sectionIds[0]);

  useEffect(() => {
    const sections = sectionIds.map((id) => document.getElementById(id)).filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visibleEntries[0]?.target?.id) {
          setActiveSection(visibleEntries[0].target.id);
        }
      },
      {
        rootMargin: "-30% 0px -45% 0px",
        threshold: [0.2, 0.4, 0.65],
      },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [sectionIds]);

  return activeSection;
}

function BrandMark({ inverted = false }) {
  return (
    <div className={cn("brand-mark", inverted && "brand-mark-inverted")} aria-label="MORCAT S.R.L. Constructora">
      <span className="brand-mark-title">MORCAT</span>
      <span className="brand-mark-subtitle">Constructora de autor</span>
    </div>
  );
}

function SectionHeading({ eyebrow, title, description, align = "left" }) {
  return (
    <div className={cn("max-w-3xl space-y-5", align === "center" && "mx-auto text-center")}>
      <span className="section-eyebrow">{eyebrow}</span>
      <h2 className="section-title">{title}</h2>
      <p className="section-copy">{description}</p>
    </div>
  );
}

function TiltSurface({ as: Component = "div", className, children, ...props }) {
  const reducedMotion = useReducedMotion();
  const surfaceRef = useRef(null);

  const handleMove = (event) => {
    if (reducedMotion || !surfaceRef.current) return;

    const rect = surfaceRef.current.getBoundingClientRect();
    const relativeX = event.clientX - rect.left;
    const relativeY = event.clientY - rect.top;
    const rotateY = ((relativeX / rect.width) - 0.5) * 10;
    const rotateX = ((relativeY / rect.height) - 0.5) * -10;

    surfaceRef.current.style.transform = `perspective(1400px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-6px)`;
    surfaceRef.current.style.setProperty("--pointer-x", `${(relativeX / rect.width) * 100}%`);
    surfaceRef.current.style.setProperty("--pointer-y", `${(relativeY / rect.height) * 100}%`);
  };

  const resetTilt = () => {
    if (!surfaceRef.current) return;
    surfaceRef.current.style.transform = "perspective(1400px) rotateX(0deg) rotateY(0deg) translateY(0px)";
    surfaceRef.current.style.setProperty("--pointer-x", "50%");
    surfaceRef.current.style.setProperty("--pointer-y", "50%");
  };

  return (
    <Component
      ref={surfaceRef}
      className={cn("tilt-surface", className)}
      onMouseMove={handleMove}
      onMouseLeave={resetTilt}
      {...(Component === "button" ? { type: "button" } : {})}
      {...props}
    >
      {children}
    </Component>
  );
}

function CountUpCard({ stat }) {
  const [ref, isVisible] = useInViewOnce({ threshold: 0.55 });
  const count = useCountUp(stat.value, isVisible);

  return (
    <div ref={ref} className="metric-card">
      <div className="metric-value">
        {count}
        {stat.suffix}
      </div>
      <div className="space-y-2">
        <p className="metric-label">{stat.label}</p>
        <p className="text-sm text-[#5e605e]">{stat.detail}</p>
      </div>
    </div>
  );
}

function Header({ activeSection }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = mobileMenuOpen ? "hidden" : previousOverflow;

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  const handleNavigation = (sectionId) => {
    scrollToSection(sectionId);
    setMobileMenuOpen(false);
  };

  return (
    <header className="site-header">
      <div className="container-premium">
        <div className="header-shell">
          <button
            type="button"
            className="shrink-0 text-left"
            onClick={() => handleNavigation("inicio")}
            aria-label="Ir al inicio"
          >
            <BrandMark />
          </button>

          <nav className="hidden items-center gap-2 xl:flex">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => handleNavigation(link.id)}
                className={cn("nav-link-chip", activeSection === link.id && "nav-link-chip-active")}
              >
                {link.name}
              </button>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <button type="button" onClick={() => handleNavigation("proyectos")} className="btn-ghost-shell">
              Ver proyectos
            </button>
            <button type="button" onClick={() => handleNavigation("estimador")} className="btn-primary-shell">
              Hablar con un ingeniero
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <button
            type="button"
            className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#1c1c1c]/10 bg-white/70 xl:hidden"
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen ? (
          <motion.div
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mobile-panel xl:hidden"
          >
            <div className="container-premium">
              <div className="surface-panel flex flex-col gap-5 p-5">
                {NAV_LINKS.map((link) => (
                  <button
                    key={link.id}
                    type="button"
                    onClick={() => handleNavigation(link.id)}
                    className={cn("mobile-nav-link", activeSection === link.id && "mobile-nav-link-active")}
                  >
                    <span>{link.name}</span>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                ))}

                <div className="grid gap-3 pt-2 sm:grid-cols-2">
                  <a
                    href={`https://wa.me/${CONTACT.whatsapp}?text=Hola, quiero conversar sobre un proyecto con MORCAT.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp-shell"
                  >
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp
                  </a>
                  <button type="button" onClick={() => handleNavigation("estimador")} className="btn-primary-shell">
                    Cotizá tu Obra
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}

function Hero() {
  const reducedMotion = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const mediaY = useTransform(scrollYProgress, [0, 1], [0, reducedMotion ? 0 : 90]);

  return (
    <section id="inicio" ref={heroRef} data-testid="hero-section" className="section-shell hero-shell">
      <div className="container-premium">
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1.02fr)_minmax(0,0.98fr)]">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="space-y-7"
          >
            <span className="section-eyebrow">Constructora local · Ushuaia · +15 años en Tierra del Fuego</span>
            <h1 className="hero-title">
              <span className="hero-title-block">Tu obra,</span>
              <span className="hero-title-block">entregada</span>
              <span className="hero-title-block hero-title-accent">a tiempo.</span>
              <span className="hero-title-block hero-title-accent">Sin vueltas.</span>
            </h1>
            <p className="hero-copy">
              Viviendas, remodelaciones y construcción en seco. Presupuesto cerrado, plazos por escrito y los dueños presentes en obra todos los días.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <button type="button" className="btn-primary-shell" onClick={() => scrollToSection("estimador")}>
                Cotizá tu Obra Hoy
                <ArrowRight className="h-4 w-4 shrink-0" />
              </button>
              <button type="button" className="btn-ghost-shell" onClick={() => scrollToSection("proceso")}>
                Mirá cómo trabajamos
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 sm:gap-4">
              {STATS.map((stat) => (
                <div key={stat.label} className="hero-stat-chip">
                  <span className="hero-stat-value">
                    {stat.value}
                    {stat.suffix}
                  </span>
                  <span className="hero-stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="relative min-h-[520px] lg:min-h-[680px]">
            <motion.div style={{ y: mediaY }} className="hero-media-frame">
              <video
                className="hero-media-image"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label="Video de proyecto residencial ejecutado por MORCAT en Ushuaia"
              >
                <source src="/video/hero.mp4" type="video/mp4" />
                <img src={IMAGES.hero} alt="Proyecto residencial contemporáneo ejecutado por MORCAT" className="hero-media-image" />
              </video>
              <div className="hero-media-overlay" />
              <div className="hero-grid-overlay" />
            </motion.div>

            <motion.div className="floating-panel floating-panel-top">
              <p className="floating-panel-label">Mesa de control</p>
              <div className="space-y-4">
                <div className="floating-line">
                  <span>Compras críticas</span>
                  <strong>Sin retrasos</strong>
                </div>
                <div className="floating-line">
                  <span>Seguimiento cliente</span>
                  <strong>Semanal</strong>
                </div>
                <div className="floating-line">
                  <span>Desvíos visibles</span>
                  <strong>En tiempo real</strong>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.25, duration: 0.7 }}
              className="floating-panel floating-panel-bottom"
            >
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#8d7448]">
                Dirección con criterio
              </span>
              <h3 className="mt-3 font-heading text-2xl text-[#1c1c1c]">Menos improvisación. Más precisión.</h3>
              <p className="mt-3 text-sm leading-6 text-[#5d605f]">
                Tu proyecto no depende de “ver qué pasa”. Depende de una secuencia de decisiones correctas.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  return (
    <section className="section-shell pb-8">
      <div className="container-premium">
        <div className="grid gap-4 lg:grid-cols-3">
          {STATS.map((stat) => (
            <CountUpCard key={stat.label} stat={stat} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Services() {
  const [activeServiceId, setActiveServiceId] = useState(SERVICES[0].id);
  const activeService = SERVICES.find((service) => service.id === activeServiceId) ?? SERVICES[0];

  return (
    <section id="servicios" className="section-shell section-padding">
      <div className="container-premium space-y-10">
        <SectionHeading
          eyebrow="Servicios"
          title="Lo que hacemos, lo hacemos bien."
          description="De viviendas nuevas a remodelaciones completas. Elegimos el sistema que más le conviene a tu proyecto, no el que más nos conviene a nosotros."
        />

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
          <div className="grid gap-5 md:grid-cols-2">
            {SERVICES.map((service, index) => {
              const Icon = service.icon;
              const isActive = service.id === activeServiceId;

              return (
                <TiltSurface
                  key={service.id}
                  as="button"
                  className={cn("service-card", isActive && "service-card-active")}
                  onMouseEnter={() => setActiveServiceId(service.id)}
                  onFocus={() => setActiveServiceId(service.id)}
                  onClick={() => setActiveServiceId(service.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="service-icon-shell">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="service-index">0{index + 1}</span>
                  </div>
                  <div className="mt-6 space-y-3 text-left">
                    <h3 className="font-heading text-3xl leading-none text-[#1c1c1c]">{service.title}</h3>
                    <p className="text-sm leading-6 text-[#616462]">{service.summary}</p>
                  </div>
                </TiltSurface>
              );
            })}
          </div>

          <div className="xl:sticky xl:top-32 xl:self-start">
            <div className="service-spotlight">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeService.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.28 }}
                  className="space-y-6"
                >
                  <div className="space-y-3">
                    <span className="section-eyebrow">Servicio destacado</span>
                    <h3 className="font-heading text-4xl text-[#1c1c1c]">{activeService.title}</h3>
                    <p className="text-base leading-7 text-[#5d605f]">{activeService.lead}</p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {activeService.metrics.map((metric) => (
                      <span key={metric} className="detail-pill">
                        {metric}
                      </span>
                    ))}
                  </div>

                  <div className="grid gap-3">
                    {activeService.outcomes.map((outcome) => (
                      <div key={outcome} className="flex items-start gap-3">
                        <CheckCircle2 className="mt-1 h-5 w-5 text-[#8d7448]" />
                        <p className="text-sm leading-6 text-[#5d605f]">{outcome}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Projects() {
  const [activeFilter, setActiveFilter] = useState("Todos");
  const [selectedProject, setSelectedProject] = useState(null);

  const visibleProjects = PROJECTS.filter(
    (project) => activeFilter === "Todos" || project.category === activeFilter,
  );

  return (
    <section id="proyectos" className="section-shell section-padding">
      <div className="container-premium space-y-10">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <SectionHeading
            eyebrow="Nuestros proyectos"
            title="Obras reales. Clientes contentos."
            description="El resultado final es nuestra mejor publicidad. Filtrá por tipo de obra y abrí cada proyecto para ver alcance, tiempos y decisiones clave."
          />

          <div className="flex flex-wrap gap-3">
            {PROJECT_FILTERS.map((filter) => (
              <button
                key={filter}
                type="button"
                onClick={() => setActiveFilter(filter)}
                className={cn("filter-chip", activeFilter === filter && "filter-chip-active")}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {visibleProjects.map((project, index) => (
            <TiltSurface
              key={project.id}
              as="button"
              className={cn(
                "project-card group",
                index === 0 && visibleProjects.length > 1 && "xl:col-span-2 project-card-featured",
              )}
              onClick={() => setSelectedProject(project)}
            >
              <img src={project.image} alt={`${project.title} en ${project.location}`} className="project-card-image" />
              <div className="project-card-overlay" />
              <div className="project-card-content">
                <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-white/75">
                  <span>{project.category}</span>
                  <span>•</span>
                  <span>{project.badge}</span>
                </div>
                <div className="mt-5 flex items-end justify-between gap-4">
                  <div className="space-y-2 text-left">
                    <h3 className="font-heading text-3xl text-white">{project.title}</h3>
                    <p className="text-sm text-white/78">{project.summary}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 shrink-0 text-white/80 transition-transform duration-300 group-hover:translate-x-1" />
                </div>
                <div className="mt-5 flex flex-wrap gap-2 text-xs text-white/80">
                  <span className="project-detail-pill">{project.area}</span>
                  <span className="project-detail-pill">{project.duration}</span>
                  <span className="project-detail-pill">{project.location}</span>
                </div>
              </div>
            </TiltSurface>
          ))}
        </div>
      </div>

      <Dialog open={Boolean(selectedProject)} onOpenChange={(open) => !open && setSelectedProject(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-[#d6cab4] bg-[#f7f2e8] p-0 text-[#1c1c1c] sm:max-w-5xl">
          {selectedProject ? (
            <div className="grid gap-0 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="relative min-h-[320px] lg:min-h-[620px]">
                <img src={selectedProject.image} alt={`${selectedProject.title} detalle visual`} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <p className="text-xs uppercase tracking-[0.26em] text-white/70">{selectedProject.type}</p>
                  <h3 className="mt-3 font-heading text-4xl">{selectedProject.title}</h3>
                  <p className="mt-2 text-sm text-white/85">{selectedProject.highlight}</p>
                </div>
              </div>

              <div className="flex flex-col gap-6 p-7 lg:p-9">
                <DialogHeader className="space-y-3 text-left">
                  <DialogTitle className="font-heading text-4xl leading-none text-[#1c1c1c]">
                    {selectedProject.title}
                  </DialogTitle>
                  <DialogDescription className="text-base leading-7 text-[#5e605f]">
                    {selectedProject.summary}
                  </DialogDescription>
                </DialogHeader>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="modal-stat-card"><span className="modal-stat-label">Ubicación</span><strong>{selectedProject.location}</strong></div>
                  <div className="modal-stat-card"><span className="modal-stat-label">Superficie</span><strong>{selectedProject.area}</strong></div>
                  <div className="modal-stat-card"><span className="modal-stat-label">Duración</span><strong>{selectedProject.duration}</strong></div>
                  <div className="modal-stat-card"><span className="modal-stat-label">Año</span><strong>{selectedProject.year}</strong></div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-sm uppercase tracking-[0.26em] text-[#8d7448]">Alcance clave</h4>
                  <div className="grid gap-3">
                    {selectedProject.scope.map((item) => (
                      <div key={item} className="flex items-start gap-3">
                        <CheckCircle2 className="mt-1 h-5 w-5 text-[#8d7448]" />
                        <p className="text-sm leading-6 text-[#5e605f]">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-auto flex flex-col gap-3 sm:flex-row">
                  <a
                    href={`https://wa.me/${CONTACT.whatsapp}?text=Hola, quiero una obra con un nivel similar a ${selectedProject.title}.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp-shell"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Quiero algo así
                  </a>
                  <button
                    type="button"
                    className="btn-ghost-shell"
                    onClick={() => {
                      setSelectedProject(null);
                      scrollToSection("estimador");
                    }}
                  >
                    Llevar al estimador
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Estimator() {
  const [projectType, setProjectType] = useState("vivienda");
  const [finish, setFinish] = useState("premium");
  const [rhythm, setRhythm] = useState("firme");
  const [area, setArea] = useState(180);

  const estimate = calculateEstimate({ projectType, area, finish, rhythm });

  const whatsappMessage = encodeURIComponent(
    `Hola MORCAT, quiero una referencia para ${PROJECT_TYPES[projectType].label.toLowerCase()} de ${area} m², terminación ${FINISH_OPTIONS[finish].label.toLowerCase()} y ritmo ${RHYTHM_OPTIONS[rhythm].label.toLowerCase()}.`,
  );

  return (
    <section id="estimador" className="section-shell section-padding">
      <div className="container-premium">
        <div className="estimator-shell">
          <div className="space-y-8">
            <SectionHeading
              eyebrow="Calculá tu obra"
              title="¿Cuánto puede costar tu idea?"
              description="Calculalo acá mismo. Es rápido, gratis y sin compromiso. Números reales para Tierra del Fuego."
            />

            <div className="surface-panel space-y-7 p-6 lg:p-8">
              <div className="space-y-3">
                <p className="control-label">Tipo de proyecto</p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(PROJECT_TYPES).map(([key, option]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setProjectType(key)}
                      className={cn("control-chip", projectType === key && "control-chip-active")}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <p className="control-label">Superficie estimada</p>
                  <span className="text-sm font-medium text-[#1c1c1c]">{area} m²</span>
                </div>
                <input
                  type="range"
                  min="40"
                  max="420"
                  step="5"
                  value={area}
                  onChange={(event) => setArea(Number(event.target.value))}
                  className="range-input"
                  aria-label="Superficie estimada"
                />
                <div className="flex justify-between text-xs uppercase tracking-[0.22em] text-[#7d7f7d]">
                  <span>40 m²</span>
                  <span>420 m²</span>
                </div>
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                <div className="space-y-3">
                  <p className="control-label">Nivel de terminación</p>
                  <div className="grid gap-3">
                    {Object.entries(FINISH_OPTIONS).map(([key, option]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFinish(key)}
                        className={cn("segmented-card", finish === key && "segmented-card-active")}
                      >
                        <span className="font-medium text-[#1c1c1c]">{option.label}</span>
                        <span className="text-sm text-[#5f625f]">{option.detail}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="control-label">Ritmo de obra</p>
                  <div className="grid gap-3">
                    {Object.entries(RHYTHM_OPTIONS).map(([key, option]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setRhythm(key)}
                        className={cn("segmented-card", rhythm === key && "segmented-card-active")}
                      >
                        <span className="font-medium text-[#1c1c1c]">{option.label}</span>
                        <span className="text-sm text-[#5f625f]">{option.detail}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="surface-panel estimator-result-panel p-6 lg:p-8">
            <div className="space-y-3">
              <span className="section-eyebrow">Resultado dinámico</span>
              <p className="estimator-value">
                {usdFormatter.format(estimate.min)}
                <span className="text-[#7d7f7d]"> — {usdFormatter.format(estimate.max)}</span>
              </p>
              <p className="estimator-reference-copy">
                Referencia preliminar para <em>{estimate.scopeLabel.toLowerCase()}</em> de{" "}
                <strong>{area} m²</strong>.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="result-tile">
                <Clock3 className="h-5 w-5 text-[#8d7448]" />
                <div>
                  <span className="result-tile-label">Plazo orientativo</span>
                  <strong>{estimate.timeline} meses</strong>
                </div>
              </div>
              <div className="result-tile">
                <Gauge className="h-5 w-5 text-[#8d7448]" />
                <div>
                  <span className="result-tile-label">Reserva sugerida</span>
                  <strong>{usdFormatter.format(estimate.reserve)}</strong>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-[#1c1c1c]">Índice de complejidad</span>
                <span className="text-[#5d605f]">{estimate.complexity}/100</span>
              </div>
              <div className="meter-track">
                <motion.div
                  key={`${projectType}-${finish}-${rhythm}-${area}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${estimate.complexity}%` }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  className="meter-fill"
                />
              </div>
              <p className="text-sm leading-6 text-[#5d605f]">{estimate.system}</p>
            </div>

            <div className="grid gap-3">
              {[
                `Tipo seleccionado: ${PROJECT_TYPES[projectType].label}.`,
                `Terminación: ${FINISH_OPTIONS[finish].label}.`,
                `Ritmo recomendado: ${RHYTHM_OPTIONS[rhythm].label}.`,
              ].map((line) => (
                <div key={line} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-1 h-5 w-5 text-[#8d7448]" />
                  <p className="text-sm leading-6 text-[#5d605f]">{line}</p>
                </div>
              ))}
            </div>

            <div className="mt-auto flex flex-col gap-3 sm:flex-row">
              <a
                href={`https://wa.me/${CONTACT.whatsapp}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp-shell"
              >
                <MessageCircle className="h-4 w-4" />
                Enviar esta referencia
              </a>
              <button type="button" onClick={() => scrollToSection("contacto")} className="btn-ghost-shell">
                Hablar con dirección
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Process() {
  const [activeStepId, setActiveStepId] = useState(PROCESS_STEPS[0].id);
  const activeStep = PROCESS_STEPS.find((step) => step.id === activeStepId) ?? PROCESS_STEPS[0];

  return (
    <section id="proceso" className="section-shell section-padding process-section">
      <div className="container-premium space-y-10">
        <SectionHeading
          eyebrow="Cómo trabajamos"
          title="Simple, transparente y sin vueltas."
          description="Cada paso tiene un responsable, una fecha y un resultado visible. Sin silencio, sin sorpresas."
        />

        <div className="grid gap-6 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
          <div className="surface-panel space-y-3 p-5 lg:p-6">
            {PROCESS_STEPS.map((step) => {
              const isActive = step.id === activeStepId;

              return (
                <button
                  key={step.id}
                  type="button"
                  onMouseEnter={() => setActiveStepId(step.id)}
                  onFocus={() => setActiveStepId(step.id)}
                  onClick={() => setActiveStepId(step.id)}
                  className={cn("process-step-button", isActive && "process-step-button-active")}
                >
                  <span className="process-step-number">{step.number}</span>
                  <span className="flex-1 text-left">
                    <span className="process-step-title">{step.title}</span>
                    <span className="process-step-summary">{step.summary}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-[#8d7448]" />
                </button>
              );
            })}
          </div>

          <div className="surface-panel process-active-panel relative overflow-hidden p-6 lg:p-8">
            <img
              src={IMAGES.blueprints}
              alt="Planificación técnica y control de obra"
              className="absolute inset-0 h-full w-full object-cover opacity-[0.16]"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#f6f1e7] via-[#f6f1e7]/92 to-[#ece1cf]/70" />
            <div className="relative z-10 space-y-6">
              <span className="section-eyebrow">Etapa activa</span>
              <h3 className="process-active-title">{activeStep.title}</h3>
              <p className="process-active-summary">{activeStep.summary}</p>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="result-tile">
                  <Layers3 className="h-5 w-5 text-[#8d7448]" />
                  <div>
                    <span className="result-tile-label">Responsable</span>
                    <strong>{activeStep.owner}</strong>
                  </div>
                </div>
                <div className="result-tile">
                  <Clock3 className="h-5 w-5 text-[#8d7448]" />
                  <div>
                    <span className="result-tile-label">Cadencia</span>
                    <strong>{activeStep.cadence}</strong>
                  </div>
                </div>
              </div>

              <div className="grid gap-3">
                {activeStep.deliverables.map((deliverable) => (
                  <div key={deliverable} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-1 h-5 w-5 text-[#8d7448]" />
                    <p className="text-sm leading-6 text-[#5d605f]">{deliverable}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section id="nosotros" className="section-shell section-padding">
      <div className="container-premium space-y-10">
        <SectionHeading
          eyebrow="Por qué elegirnos"
          title="Somos de acá. Conocemos Ushuaia."
          description="Sabemos lo que cuesta construir en el fin del mundo. El clima extremo, los gremios locales, los materiales que aguantan. Nuestros dueños están en cada obra todos los días."
        />

        <div className="grid gap-6 xl:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]">
          <div className="relative overflow-hidden rounded-[28px] border border-[#d9cfbe] bg-[#1c1c1c] shadow-[0_30px_80px_rgba(26,22,17,0.18)]">
            <img src={IMAGES.whyUs} alt="Equipo de MORCAT en obra" className="h-full min-h-[500px] w-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <span className="text-xs uppercase tracking-[0.24em] text-white/70">Dueños en obra</span>
              <h3 className="mt-3 font-heading text-4xl">Los dueños de MORCAT, en obra todos los días.</h3>
              <p className="mt-3 max-w-lg text-sm leading-6 text-white/80">
                No somos una empresa golondrina. Somos de acá y estamos presentes en cada proyecto.
              </p>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            {PILLARS.map((pillar) => {
              const Icon = pillar.icon;

              return (
                <TiltSurface key={pillar.title} className="pillar-card">
                  <div className="pillar-icon">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-heading text-3xl leading-none text-[#1c1c1c]">{pillar.title}</h3>
                    <p className="text-sm leading-6 text-[#5d605f]">{pillar.description}</p>
                  </div>
                </TiltSurface>
              );
            })}
          </div>
        </div>

        <div className="surface-panel grid gap-6 p-6 lg:grid-cols-[0.8fr_1.2fr] lg:p-8">
          <div>
            <span className="section-eyebrow">Nuestro compromiso</span>
            <h3 className="mt-3 font-heading text-4xl text-[#1c1c1c]">
              Tu tranquilidad es parte del presupuesto.
            </h3>
          </div>
          <div className="grid gap-3">
            {[
              "Garantía por escrito en todos los trabajos que realizamos. Firmamos y respaldamos.",
              "Presupuesto cerrado: el precio que acordamos es el precio final, sin incrementos sorpresa.",
              "Plazos en contrato: si hay un desvío, te lo comunicamos con tiempo y con contexto real.",
              "Los dueños de MORCAT están en obra todos los días. No mandamos a un tercero a representarnos.",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircle2 className="mt-1 h-5 w-5 text-[#8d7448]" />
                <p className="text-sm leading-6 text-[#5d605f]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="section-shell section-padding">
      <div className="container-premium space-y-10">
        <SectionHeading
          eyebrow="Testimonios"
          title="Lo que dicen nuestros clientes."
          description="No lo decimos nosotros. Lo dicen quienes ya construyeron con nosotros."
          align="center"
        />

        <div className="grid gap-5 lg:grid-cols-3">
          {TESTIMONIALS.map((testimonial) => (
            <TiltSurface key={testimonial.name} className="testimonial-card">
              <span className="testimonial-mark">“</span>
              <p className="relative z-10 text-base leading-7 text-[#4f5352]">{testimonial.quote}</p>
              <div className="relative z-10 mt-8 border-t border-[#cfc1ab] pt-5">
                <p className="font-medium text-[#1c1c1c]">{testimonial.name}</p>
                <p className="text-sm text-[#8d7448]">{testimonial.role}</p>
              </div>
            </TiltSurface>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section className="section-shell section-padding">
      <div className="container-premium">
        <div className="surface-panel p-6 lg:p-8">
          <SectionHeading
            eyebrow="Preguntas frecuentes"
            title="Lo que siempre nos preguntan."
            description="Las dudas más comunes antes de arrancar una obra. Si la tuya no está acá, escribinos."
          />

          <Accordion type="single" collapsible className="mt-8 space-y-0">
            {FAQ_DATA.map((item, index) => (
              <AccordionItem key={item.question} value={`faq-${index}`} className="border-b border-[#cfc2ae]">
                <AccordionTrigger className="py-6 text-left font-heading text-2xl text-[#1c1c1c] hover:text-[#8d7448] hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-base leading-7 text-[#5d605f]">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contacto" className="section-shell section-padding">
      <div className="container-premium">
        <div className="contact-shell">
          <div className="space-y-6">
            <span className="section-eyebrow text-[#bda06a]">Contacto</span>
            <h2 className="font-heading text-5xl text-[#f5efe6] lg:text-6xl">
              ¿Arrancamos con tu obra?
            </h2>
            <p className="contact-intro-copy">
              Contanos qué necesitás. Si todavía no tenés todo definido, mejor: ahí es donde más te podemos ayudar.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href={`https://wa.me/${CONTACT.whatsapp}?text=Hola MORCAT, quiero arrancar con mi proyecto.`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp-shell"
              >
                <MessageCircle className="h-4 w-4" />
                Escribir por WhatsApp
              </a>
              <a href={`mailto:${CONTACT.email}`} className="btn-ghost-inverted-shell">
                <Mail className="h-4 w-4" />
                Enviar email
              </a>
            </div>
          </div>

          <div className="grid gap-4">
            <div className="contact-card">
              <Phone className="h-5 w-5 text-[#bda06a]" />
              <div>
                <p className="contact-card-label">Teléfono</p>
                <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="contact-card-link">{CONTACT.phone}</a>
              </div>
            </div>

            <div className="contact-card">
              <Mail className="h-5 w-5 text-[#bda06a]" />
              <div>
                <p className="contact-card-label">Email</p>
                <a href={`mailto:${CONTACT.email}`} className="contact-card-link">{CONTACT.email}</a>
              </div>
            </div>

            <div className="contact-card">
              <MapPin className="h-5 w-5 text-[#bda06a]" />
              <div>
                <p className="contact-card-label">Base operativa</p>
                <p className="contact-card-link">{CONTACT.address}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="section-shell pb-10">
      <div className="container-premium">
        <div className="footer-shell">
          <BrandMark inverted />
          <div className="footer-meta">
            <span>© {new Date().getFullYear()} MORCAT S.R.L.</span>
            <span>•</span>
            <span>Constructora en Ushuaia, Tierra del Fuego</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FloatingDock() {
  return (
    <div className="floating-dock">
      <a
        href={`https://wa.me/${CONTACT.whatsapp}`}
        target="_blank"
        rel="noopener noreferrer"
        className="floating-dock-button"
        aria-label="WhatsApp"
      >
        <MessageCircle className="h-5 w-5" />
      </a>
      <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="floating-dock-button" aria-label="Teléfono">
        <Phone className="h-5 w-5" />
      </a>
    </div>
  );
}

function SchemaMarkup() {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://morcat.com.ar/#organization",
        name: "MORCAT S.R.L. Constructora",
        description:
          "Constructora en Ushuaia especializada en viviendas, remodelaciones, ampliaciones, construcción en seco y dirección de obra.",
        url: "https://morcat.com.ar",
        telephone: CONTACT.phone,
        email: CONTACT.email,
        address: {
          "@type": "PostalAddress",
          streetAddress: "Transporte Villarino 474",
          addressLocality: "Ushuaia",
          addressRegion: "Tierra del Fuego",
          postalCode: "9410",
          addressCountry: "AR",
        },
      },
      {
        "@type": "Service",
        "@id": "https://morcat.com.ar/#services",
        serviceType: [
          "Construcción de viviendas",
          "Remodelaciones integrales",
          "Ampliaciones",
          "Construcción en seco",
          "Dirección y control de obra",
        ],
        provider: { "@id": "https://morcat.com.ar/#organization" },
      },
    ],
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}

function App() {
  const activeSection = useActiveSection(SECTION_IDS);
  const { scrollYProgress } = useScroll();
  const progressScale = useSpring(scrollYProgress, {
    stiffness: 140,
    damping: 26,
    restDelta: 0.001,
  });

  return (
    <div className="app-shell">
      <SchemaMarkup />
      <div className="ambient-orb ambient-orb-a" />
      <div className="ambient-orb ambient-orb-b" />
      <div className="ambient-orb ambient-orb-c" />

      <div className="top-progress-track">
        <motion.div className="top-progress-bar" style={{ scaleX: progressScale }} />
      </div>

      <Header activeSection={activeSection} />

      <main className="relative z-10">
        <Hero />
        <Services />
        <Projects />
        <Estimator />
        <Process />
        <WhyUs />
        <Testimonials />
        <FAQ />
        <Contact />
      </main>

      <Footer />
      <FloatingDock />
    </div>
  );
}

export default App;
